import React, { useState, useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Code as CodeIcon,
  Search as SearchIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Storage as StorageIcon,
  Extension as ExtensionIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { ApplicationFunction, ApplicationInfo } from '../hooks/useApplicationObservatory';

interface FunctionsPanelProps {
  functions: ApplicationFunction[];
  applications: ApplicationInfo[];
  compact?: boolean;
  onFunctionClick?: (func: ApplicationFunction) => void;
}

export const FunctionsPanel: React.FC<FunctionsPanelProps> = ({
  functions,
  applications,
  compact = false,
  onFunctionClick,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [applicationFilter, setApplicationFilter] = useState<string>('all');
  const [implementationFilter, setImplementationFilter] = useState<string>('all');

  const filteredFunctions = useMemo(() => {
    return functions.filter(func => {
      const matchesSearch = 
        func.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        func.path.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesApp = applicationFilter === 'all' || func.applicationKey === applicationFilter;
      
      const matchesImplementation = 
        implementationFilter === 'all' ||
        (implementationFilter === 'implemented' && func.hasImplementation) ||
        (implementationFilter === 'unimplemented' && !func.hasImplementation) ||
        func.implementationType === implementationFilter;

      return matchesSearch && matchesApp && matchesImplementation;
    });
  }, [functions, searchQuery, applicationFilter, implementationFilter]);

  const getMethodColor = (method: string): 'success' | 'primary' | 'warning' | 'error' | 'info' => {
    switch (method.toUpperCase()) {
      case 'GET':
        return 'success';
      case 'POST':
        return 'primary';
      case 'PUT':
        return 'warning';
      case 'DELETE':
        return 'error';
      case 'PATCH':
        return 'info';
      default:
        return 'primary';
    }
  };

  const getImplementationIcon = (type: ApplicationFunction['implementationType']) => {
    switch (type) {
      case 'sql':
        return <StorageIcon fontSize="small" />;
      case 'integration':
        return <ExtensionIcon fontSize="small" />;
      case 'custom':
        return <BuildIcon fontSize="small" />;
      default:
        return <WarningIcon fontSize="small" color="warning" />;
    }
  };

  // Group functions by application
  const groupedFunctions = useMemo(() => {
    const groups: Record<string, ApplicationFunction[]> = {};
    filteredFunctions.forEach(func => {
      if (!groups[func.applicationKey]) {
        groups[func.applicationKey] = [];
      }
      groups[func.applicationKey].push(func);
    });
    return groups;
  }, [filteredFunctions]);

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon />
            <Typography variant="h6">Functions</Typography>
            <Chip label={functions.length} size="small" color="primary" />
          </Box>
        }
      />
      <CardContent>
        {/* Filters */}
        {!compact && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search functions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Application</InputLabel>
                <Select
                  value={applicationFilter}
                  label="Application"
                  onChange={(e) => setApplicationFilter(e.target.value)}
                >
                  <MenuItem value="all">All Applications</MenuItem>
                  {applications.map(app => (
                    <MenuItem key={app.key} value={app.key}>{app.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Implementation</InputLabel>
                <Select
                  value={implementationFilter}
                  label="Implementation"
                  onChange={(e) => setImplementationFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="implemented">Implemented</MenuItem>
                  <MenuItem value="unimplemented">Unimplemented</MenuItem>
                  <MenuItem value="sql">SQL</MenuItem>
                  <MenuItem value="integration">Integration</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}

        {/* Summary */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            icon={<CheckCircleIcon />}
            label={`${functions.filter(f => f.hasImplementation).length} Implemented`}
            size="small"
            color="success"
            variant="outlined"
          />
          <Chip
            icon={<WarningIcon />}
            label={`${functions.filter(f => !f.hasImplementation).length} Unimplemented`}
            size="small"
            color="warning"
            variant="outlined"
          />
          <Chip
            icon={<StorageIcon />}
            label={`${functions.filter(f => f.implementationType === 'sql').length} SQL`}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={<ExtensionIcon />}
            label={`${functions.filter(f => f.implementationType === 'integration').length} Integration`}
            size="small"
            variant="outlined"
          />
        </Box>

        {/* Functions List */}
        {Object.keys(groupedFunctions).length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No functions found
          </Typography>
        ) : (
          <Box sx={{ maxHeight: compact ? 300 : 600, overflow: 'auto' }}>
            {Object.entries(groupedFunctions).map(([appKey, appFunctions]) => {
              const app = applications.find(a => a.key === appKey);
              return (
                <Box key={appKey} sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: 'primary.main',
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {app?.name || appKey}
                    <Chip label={appFunctions.length} size="small" />
                  </Typography>
                  <List disablePadding>
                    {appFunctions.map((func) => (
                      <ListItem
                        key={func.id}
                        sx={{
                          mb: 0.5,
                          bgcolor: 'background.default',
                          borderRadius: 1,
                          border: 1,
                          borderColor: func.hasImplementation ? 'divider' : 'warning.light',
                          cursor: onFunctionClick ? 'pointer' : 'default',
                          '&:hover': onFunctionClick ? { bgcolor: 'action.hover' } : {},
                        }}
                        onClick={() => onFunctionClick?.(func)}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={func.method}
                                size="small"
                                color={getMethodColor(func.method)}
                                sx={{ minWidth: 55, fontWeight: 600 }}
                              />
                              <Typography
                                variant="body1"
                                sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                              >
                                {func.name}()
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ flex: 1, fontFamily: 'monospace' }}
                              >
                                {func.path}
                              </Typography>
                              <Tooltip title={func.implementationType || 'No implementation'}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {getImplementationIcon(func.implementationType)}
                                </Box>
                              </Tooltip>
                              {func.hasImplementation ? (
                                <CheckCircleIcon color="success" fontSize="small" />
                              ) : (
                                <WarningIcon color="warning" fontSize="small" />
                              )}
                            </Box>
                          }
                          secondary={
                            !compact && func.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                {func.description}
                              </Typography>
                            )
                          }
                        />
                        {onFunctionClick && (
                          <IconButton size="small">
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        )}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default FunctionsPanel;

